import { DragEndEvent } from "@dnd-kit/core";
import { arrayMove, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import React, { useMemo } from "react";

import { faGripDotsVertical } from "@fortawesome/pro-regular-svg-icons";

import { ButtonStandard, Icon, Text } from "@performyard/py-components";

import DragAndDrop from "../../../../../../components/DragAndDrop";

import { useEngagementSurveyQuestions } from "../../../../../../api/hooks";
import { useEngagementCustomFactors } from "../../../../../../api/hooks/defs/engagement-custom-factors";
import { useFeatureFlagQuery } from "../../../../../../hooks/useFeatureFlag";

import { ClientCustomQuestionGetResponse } from "../../../../../../api/services/defs/engagement";
import { scaleTypeMap } from "../../../../../../api/services/defs/engagement-survey-custom-questions";

type ManuallySelectedQuestionsListProps = {
	questions: Array<{ question_id: string; order: number }>;
	onChange: (questions: Array<{ question_id: string; order: number }>) => void;
	onDelete: (questionId: string) => void;
};

const QuestionRow = ({
	question,
	derivedQuestionId,
	order,
	onDelete,
	factorDisplayName,
}: {
	question: ClientCustomQuestionGetResponse;
	derivedQuestionId: string;
	order: number;
	onDelete: (questionId: string) => void;
	factorDisplayName: string;
}) => {
	// set drag handle
	const { setNodeRef, transform, transition, attributes, listeners } = useSortable({
		id: derivedQuestionId,
	});
	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
	};
	return (
		<li
			ref={setNodeRef}
			style={style}
			{...attributes}
			className="group grid h-[52px] w-full grid-cols-[24px_1fr] border border-solid border-neutral-2 bg-neutral-0"
		>
			<div
				{...listeners}
				className="group flex h-full cursor-grab items-center justify-center border-r border-solid border-neutral-2"
			>
				<Icon
					icon={faGripDotsVertical}
					size={24}
					color="neutral-7"
					dangerouslySetClassName="group-hover:text-neutral-9 group-active:cursor-grabbing"
				/>
			</div>
			<div className="flex min-w-0 flex-1 items-start justify-start gap-2 p-2">
				<div className="flex flex-col gap-2">
					<Text size="caption" weight="regular" color="neutral-9">
						{order + 1}.
					</Text>
				</div>
				<div className="grid h-full w-full grid-cols-[1fr_auto] items-center justify-between">
					<div className="flex min-w-0 flex-1 flex-col gap-1">
						<div className="w-[calc(100%-16px)] py-line-clamp-1">
							<Text size="paragraph-3" weight="regular" color="neutral-9">
								{question.question}
							</Text>
						</div>
						<div>
							<Text size="caption" weight="regular" color="neutral-6">
								{factorDisplayName}{" "}
								<Text overrideElement="span" size="caption" weight="regular" color="neutral-6">
									· {scaleTypeMap[question.scale] || "N/A"}
								</Text>
							</Text>
						</div>
					</div>
					<div className="hidden h-full items-end justify-end group-hover:flex">
						<ButtonStandard
							variant="micro"
							color="red"
							size="small"
							onClick={() => {
								onDelete(derivedQuestionId);
							}}
						>
							Remove
						</ButtonStandard>
					</div>
				</div>
			</div>
		</li>
	);
};

/**
 * handles the drag and drop of the manually selected questions list, it also prunes deleted questions from the list when on change occurs since we compare with the list of questions available instead of the given list
 */
const ManuallySelectedQuestionsList = ({ questions, onChange, onDelete }: ManuallySelectedQuestionsListProps) => {
	const { data: engagementSurveyQuestions, isLoading: isLoadingEngagementSurveyQuestions } =
		useEngagementSurveyQuestions();

	const { value: customFactorsEnabled } = useFeatureFlagQuery("ff-engagement-custom-factors", false);
	const { data: customFactorsData } = useEngagementCustomFactors({ enabled: customFactorsEnabled });
	const customFactorsMap = useMemo(() => {
		const map: Record<string, string> = {};
		if (customFactorsData?.customFactors) {
			for (const f of customFactorsData.customFactors) {
				map[f._id] = f.name;
			}
		}
		return map;
	}, [customFactorsData?.customFactors]);

	const questionsLookup = useMemo(() => new Map(questions.map((q) => [q.question_id, q])), [questions]);
	if (isLoadingEngagementSurveyQuestions) return <div>Loading...</div>;
	if (!engagementSurveyQuestions) return <div>No questions found</div>;
	const questionRows = engagementSurveyQuestions.results
		.filter((question) => questionsLookup.has(question?._id ?? question?.id ?? ""))
		.map((question, index) => ({
			...question,
			derivedQuestionId: question?._id ?? question?.id ?? "",
			order: questionsLookup.get(question?._id ?? question?.id ?? "")?.order ?? index,
		}))
		.sort((a, b) => a.order - b.order);

	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event;
		if (over) {
			const activeIndex = questionRows.findIndex((q) => q.derivedQuestionId === active.id);
			const overIndex = questionRows.findIndex((q) => q.derivedQuestionId === over.id);
			// swap the order in the array, then remap with new question order calc
			const newOrderedQuestions = arrayMove(questionRows, activeIndex, overIndex);
			onChange(newOrderedQuestions.map((q, index) => ({ question_id: q.derivedQuestionId, order: index })));
		}
	};

	if (questionRows.length === 0) {
		return null;
	}

	return (
		<DragAndDrop
			useUnorderedList
			handleOnDragEnd={handleDragEnd}
			itemIds={questionRows.map((q) => q.derivedQuestionId)}
			draggableNodeItems={questionRows.map((q, index) => (
				<QuestionRow
					key={q.derivedQuestionId}
					question={q}
					derivedQuestionId={q.derivedQuestionId}
					order={index}
					onDelete={onDelete}
					factorDisplayName={
						customFactorsEnabled && q.factor === "Custom"
							? ((q as unknown as { custom_factor?: string | null }).custom_factor &&
									customFactorsMap[
										(q as unknown as { custom_factor?: string | null }).custom_factor!
									]) ||
								"Unassigned"
							: q.factor
					}
				/>
			))}
			itemsContainerClassName="w-full list-none space-y-2"
			restrictToParentElement
			restrictToVerticalAxis
		/>
	);
};

export default ManuallySelectedQuestionsList;
